import React, { useEffect, useState, useRef } from "react";
import styles from "../../styles/Authsignup.module.css";
import {
  Link,
  Navigate,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { IconLoading } from "../../assets/icons";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { PasswordStrengthIndicator } from "./PasswordStrengthIndicator";
import { useToast } from "../../context/toastContext";
import { useAuth } from "../../context/authContext";
import useAuthedApi from "../../hooks/useAuthedApi";
import { extractErrorMessage } from "../../utils";
import DialogVerifyRegister from "../dialogs/DialogVerifyRegister";
import { useGoogleLogin } from "@react-oauth/google";
import DialogReferralCode from "../dialogs/DialogReferralCode";
import { ExclamationTriangleIcon } from "@heroicons/react/16/solid";

export const SignupForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();

  // --- NEW: Read reactivation state passed from LoginForm ---
  const reactivateEmail = location.state?.email || "";
  const isReactivateMode = location.state?.reactivate === true;

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: reactivateEmail, // prefilled from login redirect
    password: "",
    retype_password: "",
    referral_code: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isVerificationOpen, setVerificationOpen] = useState(false);
  const [error, setError] = useState("");
  const [userId, setUserId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const { authedPost } = useAuthedApi();
  const { user, fetchUser, isLoading } = useAuth();

  const [searchParams] = useSearchParams();

  const [isReferralOpen, setReferralOpen] = useState(false);
  const [googleReferral, setGoogleReferral] = useState("");

  const handleChange = (e) => {
    // If reactivate mode, prevent changing the email field
    if (isReactivateMode && e.target.id === "email") return;
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (loading) return;

    const { first_name, last_name, email, password, retype_password } =
      formData;

    const fieldValidations = {
      "First name is required": first_name.trim(),
      "Last name is required": last_name.trim(),
      "Email is required": email.trim(),
      "Password is required": password.trim(),
      "Passwords do not match": password === retype_password,
    };

    for (const errorMsg in fieldValidations) {
      if (!fieldValidations[errorMsg]) {
        setError(errorMsg);
        return;
      }
    }

    const hasMinLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!(hasMinLength && hasUppercase && hasLowercase && hasSymbol)) {
      setError("Password does not meet all strength requirements.");
      return;
    }

    const params = {};
    if (!!formData.referral_code) params.code = formData.referral_code;

    setLoading(true);
    try {
      const payload = { first_name, last_name, email, password };

      // --- NEW: Add reactivate flag when in reactivation mode ---
      if (isReactivateMode) {
        payload.reactivate = true;
      }

      const response = await authedPost(`/api/signup/`, payload, { params });

      localStorage.removeItem("verification_code_signup");
      localStorage.removeItem("verification_code_signin");

      if (response.data.user_active_immediately) {
        setSuccessMessage(
          "Your account has been reactivated. You can now login with your email and password.",
        );
        setVerificationOpen(false);
      } else if (response.data.verification_token) {
        setUserId(response.data.verification_token);
        setSuccessMessage(
          "Verification code sent to your email. Please enter the code below.",
        );
        setVerificationOpen(true);
      }
    } catch (error) {
      const data = error.response?.data;
      // If server returns the deactivated message during a normal signup attempt,
      // redirect to reactivation flow automatically
      const msg =
        typeof data === "string"
          ? data
          : data?.detail ||
            data?.message ||
            data?.error ||
            JSON.stringify(data ?? "");
      if (
        !isReactivateMode &&
        /deactivated|deleted|re-register|reactivate/i.test(msg)
      ) {
        navigate("/signup", {
          state: { reactivate: true, email: formData.email },
        });
        return;
      }
      const err = extractErrorMessage(
        data || "Signup failed. Please try again.",
      );
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReferralConfirm = (code) => {
    setGoogleReferral(code);
    setReferralOpen(false);
    googleLoginFn();
  };

  useEffect(() => {
    if (!user && !isLoading) {
      const urlReferralCode = searchParams.get("ucode");
      if (urlReferralCode) {
        setFormData((prevData) => ({
          ...prevData,
          referral_code: urlReferralCode,
        }));
        addToast("success", "Referral code applied!", 5000);
      }
    }
  }, [user, isLoading, searchParams]);

  const googleLoginFn = useGoogleLogin({
    flow: "auth-code",
    prompt: "consent",
    access_type: "offline",
    redirect_uri: "https://stg2.xenorize.com",
    scope: "openid email profile",

    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        const payload = { credential: tokenResponse.code };
        if (googleReferral) payload.code = googleReferral;

        const response = await authedPost("/api/googlelogin/", payload);

        if (response.status === 200) {
          await fetchUser();
          navigate("/dashboard");
        }
      } catch (err) {
        setError("Google signup failed. Please try again.");
      } finally {
        setLoading(false);
      }
    },
  });

  if (!isLoading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div
      className="min-h-screen min-h-[100dvh] w-full flex items-center justify-center pt-20 pb-20"
      style={{
        background: "linear-gradient(to bottom left, #413072, #08080B)",
      }}
    >
      <DialogVerifyRegister
        isVerificationOpen={isVerificationOpen}
        setVerificationOpen={setVerificationOpen}
        userId={userId}
        email={formData.email}
      />

      <DialogReferralCode
        open={isReferralOpen}
        onClose={() => setReferralOpen(false)}
        onConfirm={handleReferralConfirm}
        initialCode={formData.referral_code}
      />

      <div className="flex flex-col lg:flex-row m-8 w-full md:w-[70%] max-w-6xl bg-white rounded-xl overflow-hidden shadow-lg">
        <div className="w-full p-6 md:p-8">
          {/* --- NEW: Reactivation mode banner --- */}
          {isReactivateMode && (
            <div className="bg-[rgba(255,204,0,0.15)] gap-3 p-3 mb-4 rounded-xl border border-[#FFCC00] flex items-start">
              <ExclamationTriangleIcon className="w-full max-w-[20px] h-[20px] text-[#8F7303] flex-shrink-0" />

              <div>
                <p className="font-bold leading-[0] !mb-[1rem] block">
                  Account Deactivated
                </p>
                <p>
                  Your account with email <strong>{reactivateEmail}</strong> is
                  inactive. Fill in the form below to reactivate it.
                </p>
              </div>
            </div>
          )}

          <h1 className="text-lg lg:text-4xl font-bold text-left mb-1">
            {isReactivateMode
              ? "Reactivate Your Account"
              : "Try Free Trial For 1 Months!"}
          </h1>
          <h4 className="text-sm lg:text-base font-bold text-left text-[#7D7A7A] mb-7">
            Enter your personal details below to continue
          </h4>

          <form onSubmit={handleSubmit} className="w-full">
            <div className="flex gap-4 mb-4">
              <div className="flex-1">
                <label htmlFor="first_name" className="block text-sm mb-1">
                  First Name
                </label>
                <input
                  id="first_name"
                  type="text"
                  placeholder="First name"
                  onChange={handleChange}
                  required
                  className="mt-[2px]"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="last_name" className="block text-sm mb-1">
                  Last Name
                </label>
                <input
                  id="last_name"
                  type="text"
                  placeholder="Last name"
                  onChange={handleChange}
                  required
                  className="mt-[2px]"
                />
              </div>
            </div>

            {/* --- MODIFIED: Email field — disabled & prefilled in reactivation mode --- */}
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm mb-1">
                Email
                {isReactivateMode && (
                  <span className="ml-2 text-xs text-gray-400 font-normal">
                    (cannot be changed)
                  </span>
                )}
              </label>
              <input
                id="email"
                type="email"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
                required
                className={`mt-[2px] ${isReactivateMode ? "!bg-gray-100 !text-gray-500 !cursor-not-allowed" : ""}`}
                disabled={isReactivateMode}
                readOnly={isReactivateMode}
              />
            </div>

            {/* Referral code — hidden in reactivate mode */}
            {!isReactivateMode && (
              <div className="mb-4">
                <label htmlFor="referral_code" className="block text-sm mb-1">
                  Referral Code (optional)
                </label>
                <input
                  id="referral_code"
                  type="text"
                  value={formData.referral_code}
                  placeholder="Referral Code"
                  onChange={handleChange}
                  className="mt-[2px]"
                />
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="password" className="block text-sm mb-1">
                Password
              </label>
              <div className="relative -mt-2">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  onChange={handleChange}
                  required
                  className="!pr-[2.5rem]"
                />
                {showPassword ? (
                  <EyeSlashIcon
                    className="w-[16px] h-[16px] absolute top-[50%] translate-y-[-50%] right-4 cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  />
                ) : (
                  <EyeIcon
                    className="w-[16px] h-[16px] absolute top-[50%] translate-y-[-50%] right-4 cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  />
                )}
              </div>
            </div>

            {!!formData.password.length && (
              <div className="mt-[-1rem] mb-[1rem]">
                <PasswordStrengthIndicator password={formData.password} />
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="retype_password" className="block text-sm mb-1">
                Retype Password
              </label>
              <div className="relative -mt-2">
                <input
                  id="retype_password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Retype password"
                  onChange={handleChange}
                  required
                  className="!pr-[2.5rem]"
                />
                {showConfirmPassword ? (
                  <EyeSlashIcon
                    className="w-[16px] h-[16px] absolute top-[50%] translate-y-[-50%] right-4 cursor-pointer"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  />
                ) : (
                  <EyeIcon
                    className="w-[16px] h-[16px] absolute top-[50%] translate-y-[-50%] right-4 cursor-pointer"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  />
                )}
              </div>
            </div>

            {!isReactivateMode && (
              <>
                <div className="mb-2">
                  <label className="flex items-center text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    Send me news and promotions from The Platform
                  </label>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  By clicking sign up, I agree to The Platform's{" "}
                  <a
                    href="/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Terms of Use
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </>
            )}

            {error && <p className="text-red-600 text-xs mb-3">{error}</p>}
            {successMessage && !isVerificationOpen && (
              <p className="text-green-600 text-xs mb-3">{successMessage}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full relative overflow-hidden !py-3 !font-bold !text-base ${
                isReactivateMode
                  ? "!bg-amber-500 hover:!bg-amber-600"
                  : "!bg-[#007A78]"
              }`}
            >
              {loading ? (
                <div className="animate-spin w-fit flex justify-center items-center mx-auto">
                  <IconLoading className="w-[24px] h-[24px]" />
                </div>
              ) : isReactivateMode ? (
                "Reactivate Account"
              ) : (
                "Sign Up"
              )}
            </button>

            {/* Hide Google & divider in reactivate mode */}
            {!isReactivateMode && (
              <>
                <div className="flex items-center my-5 text-sm">
                  <hr className="flex-1 border-t border-gray-300" />
                  <span className="mx-12 text-center text-gray-500">OR</span>
                  <hr className="flex-1 border-t border-gray-300" />
                </div>

                <div className="flex flex-col md:flex-row gap-3">
                  <a
                    type="button"
                    className="flex-1 border border-[#B7BABD] text-black bg-white px-1 py-3 rounded text-sm hover:bg-blue-50 transition text-center cursor-pointer rounded-lg flex items-center justify-center"
                    onClick={() => setReferralOpen(true)}
                  >
                    <img
                      alt="Google icon"
                      src="/images/icons/icon_google.png"
                      className="w-6 h-6 mr-1"
                    />{" "}
                    Google
                  </a>
                </div>
              </>
            )}

            <div className="mt-6 text-center text-sm">
              <Link to="/login" className="text-[#302571] hover:underline">
                {isReactivateMode
                  ? "Back to Login"
                  : "Already have an account?"}
              </Link>
            </div>
          </form>
        </div>

        <img
          src="/images/images_login.png"
          alt="Hero Banner Desktop"
          className="hidden xl:block w-full xl:w-1/2 object-cover rounded-xl my-5 mr-5"
        />
      </div>
    </div>
  );
};
